import { useAppDispatch } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import { CANVAS_DEFAULT_FONT_FAMILY, OddTextOutStyles } from "@ai-teach/canvas-styles";
import type { OddTextOutElement } from "@ai-teach/types";
import { IconButton } from "@ai-teach/ui-web";
import { HiPlus } from "react-icons/hi";
import { ChoiceButtons } from "./ChoiceButtons";
import { QuestionCheckbox } from "./QuestionCheckbox";
import { gradeChoice, gradedFillStyle } from "./gradedStyles";

interface OddTextOutRendererProps {
  element: OddTextOutElement;
  isLiveMode?: boolean;
  /** Index the student picked in the play being replayed. */
  selectedId?: string | undefined;
  /** The question is over: the correct choice turns green whether or not it was picked. */
  isFrozen?: boolean | undefined;
}

export function OddTextOutRenderer({
  element,
  isLiveMode = false,
  selectedId,
  isFrozen,
}: OddTextOutRendererProps) {
  const dispatch = useAppDispatch();

  const handleToggleIsCorrectAnswer = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.stopPropagation();

    if (index > element.textChoices.length - 1) return;

    const updatedTextChoices = element.textChoices.map((choice, i) => ({
      ...choice,
      isCorrectAnswer: i === index,
    }));

    dispatch(
      updateElementProps({
        id: element.id,
        props: {
          textChoices: updatedTextChoices,
        },
      })
    );
  };

  const handleTextChoiceChange = (text: string, index: number) => {
    if (index > element.textChoices.length - 1) return;

    const updatedTextChoices = element.textChoices.map((choice, i) => ({
      ...choice,
      text: index === i ? text : choice.text,
    }));

    dispatch(
      updateElementProps({
        id: element.id,
        props: {
          textChoices: updatedTextChoices,
        },
      })
    );
  };

  const handleRemoveChoice = (index: number) => {
    if (element.textChoices.length <= 2) return;

    dispatch(
      updateElementProps({
        id: element.id,
        props: {
          textChoices: element.textChoices.filter((_, i) => i !== index),
        },
      })
    );
  };

  const handleAddChoice = () => {
    if (element.textChoices.length > 2) return;

    dispatch(
      updateElementProps({
        id: element.id,
        props: {
          textChoices: [...element.textChoices, { text: "Choice", isCorrectAnswer: false }],
        },
      })
    );
  };

  return (
    <div
      className="text-black"
      style={{
        ...OddTextOutStyles.outerDiv,
        borderRadius: element.border.cornerRadius,
        backgroundColor: element.backgroundColor ?? "transparent",
        pointerEvents: isLiveMode ? "none" : "auto",
      }}
    >
      {!isLiveMode && element.textChoices.length < 3 && (
        <div onClick={handleAddChoice} className="absolute right-2 top-2 z-10">
          <IconButton
            aria-label="Add choice"
            variant="primary"
            shape="circle"
            size="sm"
            onClick={handleAddChoice}
          >
            <HiPlus className="w-5 h-5" aria-hidden="true" title="Add choice" />
          </IconButton>
        </div>
      )}
      {element?.textChoices?.map((item, index) => {
        const graded = gradedFillStyle(
          gradeChoice(item.isCorrectAnswer, selectedId === `${index}`, isFrozen)
        );

        return (
          <div
            key={`${item.text}-${item.isCorrectAnswer}-${index}`}
            style={OddTextOutStyles.textChoice}
          >
            {!isLiveMode && (
              <ChoiceButtons
                isShown={element.textChoices.length > 2}
                handleRemoveChoice={handleRemoveChoice}
                handleToggleIsCorrectAnswer={handleToggleIsCorrectAnswer}
                isCorrectAnswer={item.isCorrectAnswer}
                index={index}
              />
            )}

            <div style={{ ...OddTextOutStyles.textContainer, ...graded }}>
              <p
                contentEditable={!isLiveMode}
                data-no-moveable-drag
                suppressContentEditableWarning
                onMouseDown={(e) => {
                  if (!isLiveMode) e.currentTarget.focus();
                }}
                onBlur={(e) => handleTextChoiceChange(e.target.innerText, index)}
                className="outline-none"
                style={{ fontFamily: CANVAS_DEFAULT_FONT_FAMILY, color: graded.color }}
              >
                {item.text}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
